import { clamp, snapClamp, computePercent, minByPredicate, percentToValue, areClose, STEP_GRID_TOLERANCE, snap } from '../utils/math.js';

/**
 * The max distance the slider thumb can be (as a percentage)
 * from a hint value before snapping.
 */
const SNAP_THRESHOLD = 0.02;
function axisForOrientation(orientation) {
    switch (orientation) {
        case 'horizontal':
            return { minEdge: 'left', maxEdge: 'right', thumbTransform: 'translateX(-50%)' };
        case 'vertical':
            return { minEdge: 'bottom', maxEdge: 'top', thumbTransform: 'translateY(50%)' };
    }
}
function computePercentFromEvent(e, root, thumb, orientation = 'horizontal') {
    const rootBounds = root.getBoundingClientRect();
    const thumbBounds = thumb.getBoundingClientRect();
    switch (orientation) {
        case 'horizontal': {
            const x = e.clientX - rootBounds.left - thumbBounds.width / 2;
            const width = rootBounds.width - thumbBounds.width;
            return x / width;
        }
        case 'vertical': {
            const y = rootBounds.bottom - e.clientY - thumbBounds.height / 2;
            const height = rootBounds.height - thumbBounds.height;
            return y / height;
        }
    }
}
function clampSliderValue(value, { min, max, step, precision }) {
    return precision === 'infinite' ? clamp(value, min, max) : snapClamp(value, step, min, max);
}
function computeValue(percent, options) {
    return clampSliderValue(percentToValue(percent, options.min, options.max), options);
}
function maybeSnapToHint(percent, hints, min, max) {
    if (!hints || hints.length === 0)
        return percent;
    const hintsPercent = hints.map((hint) => computePercent(hint, min, max));
    const [closestHint, distance] = minByPredicate(hintsPercent, (val) => Math.abs(val - percent));
    return distance <= SNAP_THRESHOLD ? closestHint : percent;
}
/**
 * Given a movement from `from` to `to`, if any hint lies strictly between them, return the hint
 * closest to `from`. Otherwise return `to`. Used so that big-step keyboard movements don't skip
 * past hints — they stop on the first one in the direction of travel.
 *
 * Hints that aren't a multiple of `step` are ignored, because the slider value always snaps to
 * a step grid — landing on an off-grid hint would produce a value that disagrees with what the
 * slider visually displays after `snapClamp` runs.
 */
function snapToHintInPath(from, to, hints, step) {
    if (!hints || hints.length === 0 || from === to)
        return to;
    const ascending = to > from;
    let closestHint = null;
    for (const hint of hints) {
        // snap() drifts by more than Number.EPSILON for hints above ~1, which would wrongly skip
        // legitimately on-grid hints — use the same grid tolerance deltaSnap does.
        if (!areClose(snap(hint, step), hint, STEP_GRID_TOLERANCE))
            continue;
        const inPath = ascending ? hint > from && hint <= to : hint < from && hint >= to;
        if (!inPath)
            continue;
        if (closestHint === null || (ascending ? hint < closestHint : hint > closestHint)) {
            closestHint = hint;
        }
    }
    return closestHint ?? to;
}
function formatterForStep(step) {
    const stepString = String(step);
    const i = stepString.indexOf('.');
    // Ensure that the number read always includes as many fraction digits as specified by `step`.
    const f = i === -1 ? 0 : stepString.length - i - 1;
    const opts = {
        minimumFractionDigits: f,
        maximumFractionDigits: f,
    };
    try {
        return new Intl.NumberFormat(undefined, {
            ...opts,
            // A more recent property that typescript doesn't know about. If the formatted number is an
            // integer, it allows ignoring the min/max fraction digits setting above. This is purely
            // progressive enhancement, and for a percent based slider, announces 0 and 1 as "0" and "1"
            // instead of "0.00" and "1.00". If the browser doesn't support this it is silently ignored.
            // @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#trailingzerodisplay
            ['trailingZeroDisplay']: 'stripIfInteger',
            // Never format as "-0". Passing "negative" is newer, which is why we try/catch and omit.
            // @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#signdisplay
            ['signDisplay']: 'negative',
        });
    }
    catch {
        return new Intl.NumberFormat(undefined, opts);
    }
}

export { axisForOrientation, clampSliderValue, computePercentFromEvent, computeValue, formatterForStep, maybeSnapToHint, snapToHintInPath };
//# sourceMappingURL=slider-primitive.utils.js.map
